import { ThemeAppearance } from '@atlaskit/lozenge';

import { formatSecondsToDuration } from 'src/components/pipelines/utils';

type StepQueueStatus = 'ASSIGNED' | 'WAITING';

type RunnersStepQueueResponse = {
  status: string;
  runner_uuid?: string;
  enqueued_on: string;
  assigned_on?: string;
  step: {
    uuid: string;
    name?: string;
    pipeline: {
      uuid: string;
    };
  };
  repository: {
    uuid: string;
    name: string;
    type: string;
    full_name: string;
    links: {
      self: {
        href: string;
      };
      html: {
        href: string;
      };
      avatar: {
        href: string;
      };
    };
  };
};

export type RunnersStepQueueStatsResponse = {
  step_count: {
    WAITING: number;
    ASSIGNED: number;
  };
  average_waiting_time_in_seconds: number;
  max_waiting_time_in_seconds: number;
};

export type FetchRunnersStepQueueResponse = {
  page: number;
  pagelen: number;
  size: number;
  values: RunnersStepQueueResponse[];
};

export const RunnersStepQueueStatus: {
  [K in StepQueueStatus]: { text: string; appearance: ThemeAppearance };
} = {
  ASSIGNED: {
    text: 'BUILDING',
    appearance: 'inprogress',
  },
  WAITING: {
    text: 'IN QUEUE',
    appearance: 'default',
  },
};

export class RunnersStepQueueItem {
  readonly repository: RunnersStepQueueResponse['repository'];
  readonly step: RunnersStepQueueResponse['step'];
  readonly status: StepQueueStatus;

  constructor(props: RunnersStepQueueResponse) {
    Object.assign(this, { ...props });
    Object.freeze(this);
  }

  get stepName(): string | undefined {
    return this.step.name;
  }

  get stepUuid(): string {
    return this.step.uuid;
  }

  get pipelineUuid(): string {
    return this.step.pipeline.uuid;
  }

  get repositoryName(): string {
    return this.repository.name;
  }

  get repositoryPath(): string {
    return this.repository.full_name;
  }

  get repositoryAvatar(): string {
    return this.repository.links.avatar.href;
  }

  get statusText(): string {
    return RunnersStepQueueStatus[this.status].text;
  }

  get statusAppearance(): ThemeAppearance {
    return RunnersStepQueueStatus[this.status].appearance;
  }
}

export class RunnersStepQueueStats {
  readonly step_count: RunnersStepQueueStatsResponse['step_count'];
  readonly average_waiting_time_in_seconds: number;
  readonly max_waiting_time_in_seconds: number;

  constructor(props: RunnersStepQueueStatsResponse) {
    Object.assign(this, { ...props });
    Object.freeze(this);
  }

  get stepsBuilding(): number {
    return this.step_count.ASSIGNED;
  }

  get stepsInQueue(): number {
    return this.step_count.ASSIGNED + this.step_count.WAITING;
  }

  get averageTimeInQueue(): string {
    return this.average_waiting_time_in_seconds
      ? formatSecondsToDuration(this.average_waiting_time_in_seconds)
      : '';
  }

  get longestTimeInQueue(): string {
    return this.max_waiting_time_in_seconds
      ? formatSecondsToDuration(this.max_waiting_time_in_seconds)
      : '';
  }
}
